import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useToast } from '@/hooks/useToast';
import { sportCategoryService } from '@/services/sportCategoryService';
import { arenaFieldService, type PhysicalField } from '@/services/arenaFieldService';
import {
  fieldDetailApiService,
  type TimeSlotResponse,
} from '@/services/fieldDetailApiService';
import { addArenaService } from '@/services/addArenaService';
import { ProductApiService } from '@/services/productApiService';
import type { AddArenaPayload, ShiftTimeSlot, TimeSlot as AddArenaTimeSlot } from '@/types/addArena';
import { getArenaSlug } from '@/utils/arena';
import { getAdminBasePath } from '@/utils/adminPaths';

interface Props {
  mode: 'create' | 'edit';
  fieldSlug?: string;
}

interface BreakTime {
  id: string;
  startTime: string;
  endTime: string;
}

interface SlotPriceRow {
  key: string;
  startTime: string; // "12:00"
  endTime: string;   // "13:00"
  label: string;     // "12.00 – 13.00"
  weekdayPrice: number;
  weekendPrice: number;
  publicId?: string;
}

const DEFAULT_SPORT_CATEGORIES = [
  { value: 'futsal', label: 'Futsal', icon: 'ph:soccer-ball-bold' },
  { value: 'mini-soccer', label: 'Mini Soccer', icon: 'ph:soccer-ball-bold' },
  { value: 'badminton', label: 'Badminton', icon: 'ph:baseball-bold' },
  { value: 'padel', label: 'Padel', icon: 'ph:tennis-ball-bold' },
  { value: 'tenis', label: 'Tenis', icon: 'ph:tennis-ball-bold' },
  { value: 'basket', label: 'Basket', icon: 'ph:basketball-bold' },
  { value: 'volleyball', label: 'Voli', icon: 'ph:volleyball-bold' },
];

const FLOOR_TYPES = [
  { value: 'vinyl', label: 'Vinyl' },
  { value: 'rumput-sintetis', label: 'Rumput Sintetis' },
  { value: 'karpet-vinyl', label: 'Karpet vinyl' },
  { value: 'interlock', label: 'Interlock' },
  { value: 'kayu', label: 'Parquet / Kayu' },
  { value: 'beton', label: 'Semen / Beton' },
  { value: 'acrylic-outdoor', label: 'Acrylic Outdoor' },
  { value: 'pvc', label: 'PVC' },
  { value: 'polyurethane', label: 'Polyurethane' },
  { value: 'epoxy-flooring', label: 'Epoxy Flooring' },
  { value: 'tanah', label: 'Tanah' },
  { value: 'pasir', label: 'Pasir' },
];

const DURATION_OPTIONS = [
  { label: '30 menit', minutes: 30 },
  { label: '1 jam', minutes: 60 },
  { label: '1,5 jam', minutes: 90 },
  { label: '2 jam', minutes: 120 },
  { label: '3 jam', minutes: 180 },
];

const HOUR_OPTIONS = Array.from({ length: 19 }, (_, i) => {
  const h = i + 6; // 06 to 24
  const str = h.toString().padStart(2, '0');
  return { value: `${str}:00`, label: `${str}.00` };
});

const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

const FieldForm: React.FC<Props> = ({ mode, fieldSlug }) => {
  const navigate = useNavigate();
  const toast = useToast();

  const arenaSlug = getArenaSlug() || localStorage.getItem('arenaSlug') || '';
  const adminBase = getAdminBasePath(arenaSlug);

  // Stepper state
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(mode === 'edit');

  // Step 1: Informasi Lapangan
  const [namaLapangan, setNamaLapangan] = useState('');
  const [deskripsi, setDeskripsi] = useState('');
  const [kategoriOlahraga, setKategoriOlahraga] = useState('futsal');
  const [jenisPermukaan, setJenisPermukaan] = useState('vinyl');
  const [ukuran, setUkuran] = useState('25 × 15 m');
  const [tipe, setTipe] = useState<'indoor' | 'outdoor'>('indoor');

  // Photos: 3 slots (0: Foto utama 16:9, 1: Galeri 1, 2: Galeri 2)
  const [photoFiles, setPhotoFiles] = useState<(File | null)[]>([null, null, null]);
  const [photoPreviews, setPhotoPreviews] = useState<(string | null)[]>([null, null, null]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [removedExistingIndices, setRemovedExistingIndices] = useState<number[]>([]);

  const fileInputRef0 = useRef<HTMLInputElement | null>(null);
  const fileInputRef1 = useRef<HTMLInputElement | null>(null);
  const fileInputRef2 = useRef<HTMLInputElement | null>(null);

  // Step 2: Waktu & Harga
  const [openHour, setOpenHour] = useState('07:00');
  const [closeHour, setCloseHour] = useState('23:00');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [isBreakOpen, setIsBreakOpen] = useState(false);
  const [breakTimes, setBreakTimes] = useState<BreakTime[]>([]);
  const [slotPrices, setSlotPrices] = useState<Record<string, { weekday: number; weekend: number; publicId?: string }>>({});

  // Step 3: Pengaturan Lainnya
  const [singleSelection, setSingleSelection] = useState(false); // false = Multiple session
  const [bookingShare, setBookingShare] = useState(false);
  const [physicalFieldSlug, setPhysicalFieldSlug] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Master data
  const [categories, setCategories] = useState(DEFAULT_SPORT_CATEGORIES);
  const [physicalFields, setPhysicalFields] = useState<PhysicalField[]>([]);
  const [apiTimeSlots, setApiTimeSlots] = useState<TimeSlotResponse[]>([]);

  // Load master data (categories & physical fields)
  useEffect(() => {
    if (!arenaSlug) return;

    sportCategoryService
      .getCategoriesForArena(arenaSlug)
      .then((cats) => {
        if (cats && cats.length) {
          const mapped = cats.map((c) => {
            const low = c.name.toLowerCase();
            let icon = 'ph:trophy-bold';
            if (low.includes('futsal') || low.includes('soccer')) icon = 'ph:soccer-ball-bold';
            else if (low.includes('badminton') || low.includes('bulutangkis')) icon = 'ph:baseball-bold';
            else if (low.includes('padel') || low.includes('tennis') || low.includes('tenis')) icon = 'ph:tennis-ball-bold';
            else if (low.includes('basket')) icon = 'ph:basketball-bold';
            else if (low.includes('voli')) icon = 'ph:volleyball-bold';

            return { value: c.slug, label: c.name, icon };
          });
          setCategories(mapped);
        }
      })
      .catch((err) => console.error('Failed to load categories:', err));

    arenaFieldService
      .getPhysicalFieldsForArena(arenaSlug)
      .then((pfs) => {
        if (pfs && pfs.length) {
          setPhysicalFields(pfs);
        }
      })
      .catch((err) => console.error('Failed to load physical fields:', err));
  }, [arenaSlug]);

  // Load existing field data in edit mode
  useEffect(() => {
    if (mode !== 'edit' || !fieldSlug || !arenaSlug) return;

    setLoadingInitial(true);
    fieldDetailApiService
      .getFieldDetail(arenaSlug, fieldSlug)
      .then((field) => {
        // Step 1
        setNamaLapangan(field.name || '');
        setDeskripsi(field.description || '');
        setKategoriOlahraga(field.sport_category_slug || 'futsal');
        setJenisPermukaan(field.surface_type || 'vinyl');
        setTipe((field.type === 'outdoor' ? 'outdoor' : 'indoor') as 'indoor' | 'outdoor');

        // Images
        if (field.images && Array.isArray(field.images.images)) {
          setExistingImages(field.images.images);
        }

        // Physical field & booking share
        const isShared = (field.shared_field_count || 1) > 1 || Boolean(field.physical_field_slug);
        setBookingShare(isShared);
        setPhysicalFieldSlug(field.physical_field_slug || '');

        // Status
        if (field.is_active !== undefined) {
          setIsActive(field.is_active);
        }

        // Config
        setSingleSelection(field.config?.single_selection ?? false);

        if (field.config?.break_times && Array.isArray(field.config.break_times)) {
          const parsedBreaks: BreakTime[] = field.config.break_times.map((bt, idx) => ({
            id: `break-${idx}`,
            startTime: bt.start_time?.slice(0, 5) || '12:00',
            endTime: bt.end_time?.slice(0, 5) || '13:00',
          }));
          setBreakTimes(parsedBreaks);
          if (parsedBreaks.length > 0) setIsBreakOpen(true);
        }

        // Step 2 Schedule & Time slots
        setApiTimeSlots(field.time_slots || []);

        if (field.time_slots && field.time_slots.length > 0) {
          const slots = field.time_slots;
          const sorted = [...slots].sort((a, b) => a.start_time.localeCompare(b.start_time));
          const start = sorted[0]?.start_time?.slice(0, 5) || '07:00';
          const end = sorted[sorted.length - 1]?.end_time?.slice(0, 5) || '23:00';
          setOpenHour(start);
          setCloseHour(end);

          if (sorted[0]?.duration_minutes) {
            setDurationMinutes(sorted[0].duration_minutes);
          }

          // Populate slotPrices
          const pricesObj: Record<string, { weekday: number; weekend: number; publicId?: string }> = {};
          slots.forEach((s) => {
            const timeKey = `${s.start_time.slice(0, 5)}-${s.end_time.slice(0, 5)}`;
            const isWeekend = s.day_of_week === 'saturday' || s.day_of_week === 'sunday';
            const price = s.base_price || 0;

            if (!pricesObj[timeKey]) {
              pricesObj[timeKey] = { weekday: 0, weekend: 0, publicId: s.public_id };
            }
            if (isWeekend) {
              pricesObj[timeKey].weekend = price;
            } else {
              pricesObj[timeKey].weekday = price;
            }
          });
          setSlotPrices(pricesObj);
        }
      })
      .catch((err) => {
        console.error('Failed to load field detail:', err);
        toast.error('Gagal memuat data lapangan');
      })
      .finally(() => {
        setLoadingInitial(false);
      });
  }, [mode, fieldSlug, arenaSlug]);

  // Photo handlers
  const handlePhotoSelect = (slotIdx: number, file: File) => {
    const updatedFiles = [...photoFiles];
    updatedFiles[slotIdx] = file;
    setPhotoFiles(updatedFiles);

    const updatedPreviews = [...photoPreviews];
    updatedPreviews[slotIdx] = URL.createObjectURL(file);
    setPhotoPreviews(updatedPreviews);
  };

  const handleRemovePhoto = (slotIdx: number) => {
    // If it's an existing image index from edit mode
    if (existingImages[slotIdx] && !photoFiles[slotIdx]) {
      setRemovedExistingIndices((prev) => [...prev, slotIdx]);
    }

    const updatedFiles = [...photoFiles];
    updatedFiles[slotIdx] = null;
    setPhotoFiles(updatedFiles);

    const updatedPreviews = [...photoPreviews];
    if (updatedPreviews[slotIdx] && !existingImages[slotIdx]) {
      URL.revokeObjectURL(updatedPreviews[slotIdx]!);
    }
    updatedPreviews[slotIdx] = null;
    setPhotoPreviews(updatedPreviews);
  };

  // Compute session rows based on openHour, closeHour, durationMinutes, and breakTimes
  const sessionRows = useMemo(() => {
    const rows: SlotPriceRow[] = [];
    const [startH, startM] = openHour.split(':').map(Number);
    const [endH, endM] = closeHour.split(':').map(Number);

    const startTotal = (startH || 0) * 60 + (startM || 0);
    const endTotal = (endH || 0) * 60 + (endM || 0);

    if (endTotal <= startTotal || durationMinutes <= 0) return rows;

    let current = startTotal;
    while (current + durationMinutes <= endTotal) {
      const next = current + durationMinutes;

      const sH = Math.floor(current / 60).toString().padStart(2, '0');
      const sM = (current % 60).toString().padStart(2, '0');
      const eH = Math.floor(next / 60).toString().padStart(2, '0');
      const eM = (next % 60).toString().padStart(2, '0');

      const startTimeStr = `${sH}:${sM}`;
      const endTimeStr = `${eH}:${eM}`;
      const key = `${startTimeStr}-${endTimeStr}`;
      const label = `${sH}.${sM} – ${eH}.${eM}`;

      // Check if slot overlaps with any active break time
      const isBreak = breakTimes.some((bt) => {
        const [bStartH, bStartM] = bt.startTime.split(':').map(Number);
        const [bEndH, bEndM] = bt.endTime.split(':').map(Number);
        const bStart = (bStartH || 0) * 60 + (bStartM || 0);
        const bEnd = (bEndH || 0) * 60 + (bEndM || 0);
        return current < bEnd && next > bStart;
      });

      if (!isBreak) {
        const existing = slotPrices[key];
        rows.push({
          key,
          startTime: startTimeStr,
          endTime: endTimeStr,
          label,
          weekdayPrice: existing?.weekday ?? 0,
          weekendPrice: existing?.weekend ?? 0,
          publicId: existing?.publicId,
        });
      }

      current = next;
    }

    return rows;
  }, [openHour, closeHour, durationMinutes, breakTimes, slotPrices]);

  // Update a single slot's price
  const handleUpdatePrice = (key: string, type: 'weekday' | 'weekend', value: number) => {
    setSlotPrices((prev) => ({
      ...prev,
      [key]: {
        weekday: type === 'weekday' ? value : (prev[key]?.weekday ?? 0),
        weekend: type === 'weekend' ? value : (prev[key]?.weekend ?? 0),
        publicId: prev[key]?.publicId,
      },
    }));
  };

  // Quick action: apply first row's prices to all rows
  const handleApplyFirstRowToAll = () => {
    if (sessionRows.length === 0) return;
    const firstRow = sessionRows[0];
    const wPrice = slotPrices[firstRow.key]?.weekday || 0;
    const wePrice = slotPrices[firstRow.key]?.weekend || 0;

    const newPrices = { ...slotPrices };
    sessionRows.forEach((r) => {
      newPrices[r.key] = {
        weekday: wPrice,
        weekend: wePrice,
        publicId: newPrices[r.key]?.publicId,
      };
    });
    setSlotPrices(newPrices);
    toast.success('Harga sesi pertama berhasil diterapkan ke semua sesi');
  };

  // Step navigation validations
  const handleNextStep = () => {
    if (step === 1) {
      if (!namaLapangan.trim()) {
        toast.error('Nama lapangan wajib diisi');
        return;
      }
      if (!kategoriOlahraga) {
        toast.error('Kategori olahraga wajib dipilih');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (sessionRows.length === 0) {
        toast.error('Tidak ada sesi yang dihasilkan dari jam operasional ini');
        return;
      }
      // Check if any slot has 0 price
      const hasZero = sessionRows.some(
        (r) => (slotPrices[r.key]?.weekday ?? 0) <= 0 || (slotPrices[r.key]?.weekend ?? 0) <= 0
      );
      if (hasZero) {
        toast.error('Semua sesi harus memiliki harga weekday & weekend');
        return;
      }
      setStep(3);
    }
  };

  // Final submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // 1. Calculate kept existing images (for edit mode)
      const keptImages = existingImages.filter((_, idx) => !removedExistingIndices.includes(idx));

      // 2. Prepare new image files to upload
      const newFiles = photoFiles.filter((f): f is File => f !== null);

      // 3. Prepare time slots array
      const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      const weekends = ['saturday', 'sunday'];

      if (mode === 'create') {
        const shifts: ShiftTimeSlot[] = [];
        let shiftCounter = 1;

        // Create shift for weekdays
        weekdays.forEach((day) => {
          const daySlots: AddArenaTimeSlot[] = sessionRows.map((row, idx) => {
            const price = slotPrices[row.key]?.weekday || 0;
            return {
              base_price: price,
              duration_minutes: durationMinutes,
              start_time: `${row.startTime}:00`,
              end_time: `${row.endTime}:00`,
              slot_order: idx + 1,
              is_active: true,
              is_available: true,
              holiday_price: price,
              weekend_price: price,
              dp_price: Math.round(price / 2),
            };
          });

          const basePrice = daySlots[0]?.base_price || 0;

          shifts.push({
            shift_name: `Shift ${shiftCounter}`,
            day_of_week: day,
            start_time: `${openHour}:00`,
            end_time: `${closeHour}:00`,
            slot_duration_minutes: durationMinutes,
            base_price_per_slot: basePrice,
            weekend_multiplier: 1,
            holiday_multiplier: 1,
            currency: 'IDR',
            is_active: true,
            min_booking_duration_minutes: 1,
            max_booking_duration_minutes: 1,
            time_slots: daySlots,
          });
          shiftCounter++;
        });

        // Create shift for weekends
        weekends.forEach((day) => {
          const daySlots: AddArenaTimeSlot[] = sessionRows.map((row, idx) => {
            const price = slotPrices[row.key]?.weekend || 0;
            return {
              base_price: price,
              duration_minutes: durationMinutes,
              start_time: `${row.startTime}:00`,
              end_time: `${row.endTime}:00`,
              slot_order: idx + 1,
              is_active: true,
              is_available: true,
              holiday_price: price,
              weekend_price: price,
              dp_price: Math.round(price / 2),
            };
          });

          const basePrice = daySlots[0]?.base_price || 0;

          shifts.push({
            shift_name: `Shift ${shiftCounter}`,
            day_of_week: day,
            start_time: `${openHour}:00`,
            end_time: `${closeHour}:00`,
            slot_duration_minutes: durationMinutes,
            base_price_per_slot: basePrice,
            weekend_multiplier: 1,
            holiday_multiplier: 1,
            currency: 'IDR',
            is_active: true,
            min_booking_duration_minutes: 1,
            max_booking_duration_minutes: 1,
            time_slots: daySlots,
          });
          shiftCounter++;
        });

        const selectedPhysicalField = physicalFields.find((f) => f.slug === physicalFieldSlug);

        const payload: AddArenaPayload = {
          name: namaLapangan,
          field_slug: generateSlug(namaLapangan),
          description: deskripsi,
          sport_category_slug: kategoriOlahraga,
          type: tipe,
          surface_type: jenisPermukaan,
          capacity: selectedPhysicalField?.capacity || 0,
          is_dp_enabled: true,
          dp_per_session: Math.round((sessionRows[0] ? slotPrices[sessionRows[0].key]?.weekday || 0 : 0) / 2),
          price_per_session: sessionRows[0] ? slotPrices[sessionRows[0].key]?.weekday || 0 : 0,
          physical_field_slug: bookingShare ? physicalFieldSlug : '',
          physical_field_name: bookingShare ? selectedPhysicalField?.name || '' : '',
          images: {},
          time_slots: shifts,
          is_active: isActive,
          config: {
            single_selection: singleSelection,
            break_times: breakTimes.map((bt) => ({
              start_time: bt.startTime,
              end_time: bt.endTime,
            })),
            interval_minutes: 0,
          },
        };

        const res = await addArenaService.createFieldWithImages(arenaSlug, payload, newFiles);
        if (res.success) {
          toast.success('Lapangan berhasil ditambahkan!');
          navigate(`${adminBase}/field`);
        } else {
          toast.error(res.message || 'Gagal menambahkan lapangan');
        }
      } else {
        // Edit Mode
        const updateSlots: Array<{
          public_id?: string;
          day_of_week: string;
          start_time: string;
          end_time: string;
          duration_minutes: number;
          base_price: number;
          slot_order: number;
          dp_price: number;
          is_available: boolean;
        }> = [];

        // For weekdays
        weekdays.forEach((day) => {
          sessionRows.forEach((row, idx) => {
            const price = slotPrices[row.key]?.weekday || 0;
            // Match existing publicId if any
            const existingPublicId = apiTimeSlots.find(
              (s) =>
                s.start_time.slice(0, 5) === row.startTime &&
                s.end_time.slice(0, 5) === row.endTime &&
                s.day_of_week === day
            )?.public_id;

            updateSlots.push({
              public_id: existingPublicId,
              day_of_week: day,
              start_time: `${row.startTime}:00`,
              end_time: `${row.endTime}:00`,
              duration_minutes: durationMinutes,
              base_price: price,
              slot_order: idx + 1,
              dp_price: Math.round(price / 2),
              is_available: true,
            });
          });
        });

        // For weekends
        weekends.forEach((day) => {
          sessionRows.forEach((row, idx) => {
            const price = slotPrices[row.key]?.weekend || 0;
            const existingPublicId = apiTimeSlots.find(
              (s) =>
                s.start_time.slice(0, 5) === row.startTime &&
                s.end_time.slice(0, 5) === row.endTime &&
                s.day_of_week === day
            )?.public_id;

            updateSlots.push({
              public_id: existingPublicId,
              day_of_week: day,
              start_time: `${row.startTime}:00`,
              end_time: `${row.endTime}:00`,
              duration_minutes: durationMinutes,
              base_price: price,
              slot_order: idx + 1,
              dp_price: Math.round(price / 2),
              is_available: true,
            });
          });
        });

        const payload = {
          name: namaLapangan,
          description: deskripsi,
          capacity: 0,
          sport_category_slug: kategoriOlahraga,
          type: tipe,
          surface_type: jenisPermukaan,
          physical_field_slug: bookingShare ? physicalFieldSlug : undefined,
          time_slots: updateSlots,
          keep_images: keptImages,
          is_active: isActive,
          config: {
            single_selection: singleSelection,
            break_times: breakTimes.map((bt) => ({
              start_time: bt.startTime,
              end_time: bt.endTime,
            })),
            interval_minutes: 0,
          },
        };

        const res = await fieldDetailApiService.updateField(arenaSlug, fieldSlug!, payload, newFiles);
        if (res.success) {
          // Update status if needed
          try {
            await ProductApiService.updateFieldVisibility(fieldSlug!, isActive);
          } catch {}

          toast.success('Lapangan berhasil diperbarui!');
          navigate(`${adminBase}/field`);
        } else {
          toast.error(res.message || 'Gagal memperbarui lapangan');
        }
      }
    } catch (err: any) {
      console.error('Error saving field:', err);
      toast.error(err?.message || 'Terjadi kesalahan saat menyimpan lapangan');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingInitial) {
    return (
      <div className="p-16 flex flex-col items-center justify-center gap-3">
        <Icon icon="ph:spinner" className="w-8 h-8 text-[#FC480D] animate-spin" />
        <span className="text-sm text-gray-500">Memuat data lapangan...</span>
      </div>
    );
  }

  // Get image source for slot (0, 1, 2)
  const getSlotImageSrc = (slotIdx: number) => {
    if (photoPreviews[slotIdx]) return photoPreviews[slotIdx];
    if (existingImages[slotIdx] && !removedExistingIndices.includes(slotIdx)) {
      return existingImages[slotIdx];
    }
    return null;
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-8xl mx-auto pb-10">
      {/* Top Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`${adminBase}/field`)}
            className="w-9 h-9 rounded-full border border-[#E0E6ED] bg-white hover:bg-gray-50 flex items-center justify-center text-[#0E1726] transition-colors shadow-2xs"
            aria-label="Kembali"
          >
            <Icon icon="ph:arrow-left-bold" className="w-4 h-4" />
          </button>
          <h1 className="text-xl sm:text-2xl font-semibold text-[#0E1726]">
            {mode === 'create' ? 'Tambah Lapangan' : 'Edit Lapangan'}
          </h1>
        </div>

        <button
          type="button"
          onClick={() => navigate(`${adminBase}/field`)}
          className="text-sm font-semibold text-[#8C8C8C] hover:text-[#0E1726] transition-colors"
        >
          Batal
        </button>
      </div>

      {/* Stepper Card */}
      <div className="rounded-2xl border border-[#E0E6ED] bg-white p-3 sm:p-4 shadow-sm flex items-center gap-2 sm:gap-4">
        {/* Step 1 */}
        <div
          onClick={() => setStep(1)}
          className={`flex-1 flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-xl transition-all cursor-pointer ${
            step === 1
              ? 'bg-[#FFF5EE] border border-[#FC480D]/20 text-[#FC480D]'
              : step > 1
                ? 'text-[#0E1726] hover:bg-gray-50'
                : 'text-gray-400'
          }`}
        >
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold text-xs shrink-0 ${
              step > 1
                ? 'bg-[#FC480D] text-white'
                : step === 1
                  ? 'border border-[#FC480D] text-[#FC480D] bg-white'
                  : 'border border-gray-300 text-gray-400 bg-white'
            }`}
          >
            {step > 1 ? <Icon icon="ph:check-bold" className="w-4 h-4" /> : '1'}
          </div>
          <div className="min-w-0">
            <span
              className={`block text-[10.5px] font-semibold leading-none ${
                step === 1 ? 'text-[#FC480D]' : 'text-gray-400'
              }`}
            >
              Langkah 1
            </span>
            <span
              className={`block text-xs sm:text-sm font-semibold truncate mt-0.5 ${
                step === 1 ? 'text-[#FC480D]' : 'text-[#0E1726]'
              }`}
            >
              Informasi Lapangan
            </span>
          </div>
        </div>

        {/* Step 2 */}
        <div
          onClick={() => step > 1 && setStep(2)}
          className={`flex-1 flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-xl transition-all ${
            step === 2
              ? 'bg-[#FFF5EE] border border-[#FC480D]/20 text-[#FC480D]'
              : step > 2
                ? 'text-[#0E1726] cursor-pointer hover:bg-gray-50'
                : 'text-gray-400'
          }`}
        >
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold text-xs shrink-0 ${
              step > 2
                ? 'bg-[#FC480D] text-white'
                : step === 2
                  ? 'border border-[#FC480D] text-[#FC480D] bg-white'
                  : 'border border-gray-300 text-gray-400 bg-white'
            }`}
          >
            {step > 2 ? <Icon icon="ph:check-bold" className="w-4 h-4" /> : '2'}
          </div>
          <div className="min-w-0">
            <span
              className={`block text-[10.5px] font-semibold leading-none ${
                step === 2 ? 'text-[#FC480D]' : 'text-gray-400'
              }`}
            >
              Langkah 2
            </span>
            <span
              className={`block text-xs sm:text-sm font-semibold truncate mt-0.5 ${
                step === 2 ? 'text-[#FC480D]' : step > 2 ? 'text-[#0E1726]' : 'text-gray-500'
              }`}
            >
              Waktu & Harga
            </span>
          </div>
        </div>

        {/* Step 3 */}
        <div
          className={`flex-1 flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-xl transition-all ${
            step === 3
              ? 'bg-[#FFF5EE] border border-[#FC480D]/20 text-[#FC480D]'
              : 'text-gray-400'
          }`}
        >
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold text-xs shrink-0 ${
              step === 3
                ? 'border border-[#FC480D] text-[#FC480D] bg-white'
                : 'border border-gray-300 text-gray-400 bg-white'
            }`}
          >
            3
          </div>
          <div className="min-w-0">
            <span
              className={`block text-[10.5px] font-semibold leading-none ${
                step === 3 ? 'text-[#FC480D]' : 'text-gray-400'
              }`}
            >
              Langkah 3
            </span>
            <span
              className={`block text-xs sm:text-sm font-semibold truncate mt-0.5 ${
                step === 3 ? 'text-[#FC480D]' : 'text-gray-500'
              }`}
            >
              Pengaturan Lainnya
            </span>
          </div>
        </div>
      </div>

      {/* Main Card Content */}
      <div className="rounded-2xl border border-[#E0E6ED] bg-white p-6 sm:p-8 shadow-sm">
        {/* ================= STEP 1: INFORMASI LAPANGAN ================= */}
        {step === 1 && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column: FOTO */}
              <div>
                <h3 className="text-xs font-semibold tracking-wider text-[#8C8C8C] uppercase mb-3">
                  FOTO
                </h3>

                {/* Hidden File Inputs */}
                <input
                  type="file"
                  ref={fileInputRef0}
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handlePhotoSelect(0, f);
                  }}
                />
                <input
                  type="file"
                  ref={fileInputRef1}
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handlePhotoSelect(1, f);
                  }}
                />
                <input
                  type="file"
                  ref={fileInputRef2}
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handlePhotoSelect(2, f);
                  }}
                />

                {/* Main Photo Box (16:9) */}
                <div
                  onClick={() => fileInputRef0.current?.click()}
                  className="aspect-[16/9] w-full rounded-2xl border-2 border-dashed border-[#D5D5D5] bg-[#F6F6F6] hover:bg-gray-100/70 transition-colors relative overflow-hidden flex flex-col items-center justify-center cursor-pointer group"
                >
                  {getSlotImageSrc(0) ? (
                    <>
                      <img
                        src={getSlotImageSrc(0)!}
                        alt="Foto utama"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePhoto(0);
                        }}
                        className="absolute top-3 right-3 w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                        aria-label="Hapus foto utama"
                      >
                        <Icon icon="ph:trash-bold" className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-1.5 text-center pointer-events-none">
                      <Icon icon="ph:image-square" className="w-8 h-8 text-[#98A2B3]" />
                      <span className="text-xs text-[#919191] font-medium">Foto utama 16:9</span>
                    </div>
                  )}
                </div>

                {/* Two Gallery Boxes Below */}
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {/* Gallery 1 */}
                  <div
                    onClick={() => fileInputRef1.current?.click()}
                    className="aspect-[16/9] rounded-xl border border-dashed border-[#D5D5D5] bg-[#F6F6F6] hover:bg-gray-100/70 transition-colors relative overflow-hidden flex flex-col items-center justify-center cursor-pointer group"
                  >
                    {getSlotImageSrc(1) ? (
                      <>
                        <img
                          src={getSlotImageSrc(1)!}
                          alt="Galeri 1"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemovePhoto(1);
                          }}
                          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                          aria-label="Hapus foto galeri 1"
                        >
                          <Icon icon="ph:trash-bold" className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-1 text-center pointer-events-none">
                        <Icon icon="ph:image-square" className="w-5 h-5 text-[#98A2B3]" />
                        <span className="text-[11px] text-[#919191] font-medium">Galeri</span>
                      </div>
                    )}
                  </div>

                  {/* Gallery 2 */}
                  <div
                    onClick={() => fileInputRef2.current?.click()}
                    className="aspect-[16/9] rounded-xl border border-dashed border-[#D5D5D5] bg-[#F6F6F6] hover:bg-gray-100/70 transition-colors relative overflow-hidden flex flex-col items-center justify-center cursor-pointer group"
                  >
                    {getSlotImageSrc(2) ? (
                      <>
                        <img
                          src={getSlotImageSrc(2)!}
                          alt="Galeri 2"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemovePhoto(2);
                          }}
                          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                          aria-label="Hapus foto galeri 2"
                        >
                          <Icon icon="ph:trash-bold" className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-1 text-center pointer-events-none">
                        <Icon icon="ph:image-square" className="w-5 h-5 text-[#98A2B3]" />
                        <span className="text-[11px] text-[#919191] font-medium">Galeri</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Photo Instruction Hint */}
                <p className="text-[11px] text-[#8C8C8C] mt-3 leading-relaxed">
                  Rasio 16:9, minimal 1280 × 720 px. Foto utama tampil di kartu dan aplikasi pelanggan.
                </p>
              </div>

              {/* Right Column: INFORMASI */}
              <div>
                <h3 className="text-xs font-semibold tracking-wider text-[#8C8C8C] uppercase mb-4">
                  INFORMASI
                </h3>

                <div className="space-y-4">
                  {/* Nama Lapangan */}
                  <div>
                    <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">
                      Nama lapangan
                    </label>
                    <input
                      type="text"
                      value={namaLapangan}
                      onChange={(e) => setNamaLapangan(e.target.value)}
                      placeholder="Contoh: Lapangan 5"
                      className="w-full rounded-xl border border-[#E0E6ED] bg-white px-3.5 py-2.5 text-sm text-[#0E1726] placeholder:text-gray-400 focus:border-[#FC480D] focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Deskripsi */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-[#0E1726]">Deskripsi</label>
                      <span className="text-[11px] text-[#8C8C8C]">{deskripsi.length}/500</span>
                    </div>
                    <textarea
                      value={deskripsi}
                      onChange={(e) => setDeskripsi(e.target.value.slice(0, 500))}
                      rows={3}
                      placeholder="Ceritakan keunggulan lapangan, misalnya kondisi permukaan, pencahayaan, atau aturan pemakaian."
                      className="w-full rounded-xl border border-[#E0E6ED] bg-white px-3.5 py-2.5 text-sm text-[#0E1726] placeholder:text-gray-400 focus:border-[#FC480D] focus:outline-none transition-colors h-24 resize-none"
                    />
                  </div>

                  {/* Olahraga (Selectable Chips) */}
                  <div>
                    <label className="block text-xs font-semibold text-[#0E1726] mb-2">Olahraga</label>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((c) => {
                        const isSelected = kategoriOlahraga === c.value;
                        return (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => setKategoriOlahraga(c.value)}
                            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                              isSelected
                                ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                                : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <Icon icon={c.icon} className="w-3.5 h-3.5" />
                            <span>{c.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Jenis Permukaan & Ukuran */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">
                        Jenis permukaan
                      </label>
                      <select
                        value={jenisPermukaan}
                        onChange={(e) => setJenisPermukaan(e.target.value)}
                        className="w-full rounded-xl border border-[#E0E6ED] bg-white px-3 py-2.5 text-sm text-[#0E1726] focus:border-[#FC480D] focus:outline-none transition-colors"
                      >
                        {FLOOR_TYPES.map((f) => (
                          <option key={f.value} value={f.value}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">
                        Ukuran
                      </label>
                      <input
                        type="text"
                        value={ukuran}
                        onChange={(e) => setUkuran(e.target.value)}
                        placeholder="Contoh: 25 × 15 m"
                        className="w-full rounded-xl border border-[#E0E6ED] bg-white px-3.5 py-2.5 text-sm text-[#0E1726] placeholder:text-gray-400 focus:border-[#FC480D] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Tipe (Indoor / Outdoor) */}
                  <div>
                    <label className="block text-xs font-semibold text-[#0E1726] mb-2">Tipe</label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setTipe('indoor')}
                        className={`rounded-full px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                          tipe === 'indoor'
                            ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                            : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon icon="ph:house-line-bold" className="w-3.5 h-3.5" />
                        <span>Indoor</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setTipe('outdoor')}
                        className={`rounded-full px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                          tipe === 'outdoor'
                            ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                            : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon icon="ph:sun-bold" className="w-3.5 h-3.5" />
                        <span>Outdoor</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Footer - Step 1 */}
            <div className="flex items-center justify-between pt-6 border-t border-[#F5F6F7]">
              <div />
              <div className="flex items-center gap-4">
                <span className="text-xs text-[#8C8C8C]">Langkah 1 dari 3</span>
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white font-semibold text-sm px-7 py-2.5 shadow-xs transition-colors"
                >
                  Lanjut →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ================= STEP 2: WAKTU & HARGA ================= */}
        {step === 2 && (
          <div className="space-y-6">
            {/* JAM OPERASIONAL */}
            <div>
              <h3 className="text-xs font-semibold tracking-wider text-[#8C8C8C] uppercase mb-3">
                JAM OPERASIONAL
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">Buka</label>
                  <select
                    value={openHour}
                    onChange={(e) => setOpenHour(e.target.value)}
                    className="w-full rounded-xl border border-[#E0E6ED] bg-white px-3.5 py-2.5 text-sm text-[#0E1726] focus:border-[#FC480D] focus:outline-none transition-colors"
                  >
                    {HOUR_OPTIONS.slice(0, -1).map((h) => (
                      <option key={h.value} value={h.value}>
                        {h.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">Tutup</label>
                  <select
                    value={closeHour}
                    onChange={(e) => setCloseHour(e.target.value)}
                    className="w-full rounded-xl border border-[#E0E6ED] bg-white px-3.5 py-2.5 text-sm text-[#0E1726] focus:border-[#FC480D] focus:outline-none transition-colors"
                  >
                    {HOUR_OPTIONS.map((h) => (
                      <option key={h.value} value={h.value}>
                        {h.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Durasi per sesi */}
            <div>
              <label className="block text-xs font-semibold text-[#0E1726] mb-2">
                Durasi per sesi
              </label>
              <div className="flex flex-wrap gap-2">
                {DURATION_OPTIONS.map((d) => {
                  const isSelected = durationMinutes === d.minutes;
                  return (
                    <button
                      key={d.minutes}
                      type="button"
                      onClick={() => setDurationMinutes(d.minutes)}
                      className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
                        isSelected
                          ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                          : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* HARGA PER SESI */}
            <div className="pt-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold tracking-wider text-[#8C8C8C] uppercase">
                  HARGA PER SESI
                </h3>

                <button
                  type="button"
                  onClick={() => setIsBreakOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#FC480D] hover:underline"
                >
                  <Icon icon="ph:clock" className="w-4 h-4" />
                  <span>{isBreakOpen ? 'Sembunyikan jam break' : 'Atur jam break'}</span>
                </button>
              </div>

              {/* Break times editor drawer/box */}
              {isBreakOpen && (
                <div className="mb-4 p-4 rounded-xl border border-[#E0E6ED] bg-gray-50/70 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#0E1726]">Waktu Istirahat (Break)</span>
                    <button
                      type="button"
                      onClick={() => {
                        setBreakTimes((prev) => [
                          ...prev,
                          { id: Date.now().toString(), startTime: '12:00', endTime: '13:00' },
                        ]);
                      }}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#FC480D] hover:text-[#E8490F]"
                    >
                      <Icon icon="ph:plus-bold" className="w-3.5 h-3.5" />
                      <span>Tambah Break</span>
                    </button>
                  </div>

                  {breakTimes.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">
                      Tidak ada jam break. Lapangan beroperasi penuh dari jam buka hingga tutup.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {breakTimes.map((bt) => (
                        <div key={bt.id} className="flex items-center gap-3">
                          <input
                            type="time"
                            value={bt.startTime}
                            onChange={(e) => {
                              const val = e.target.value;
                              setBreakTimes((prev) =>
                                prev.map((item) => (item.id === bt.id ? { ...item, startTime: val } : item))
                              );
                            }}
                            className="rounded-lg border border-[#E0E6ED] bg-white px-2.5 py-1.5 text-xs text-[#0E1726]"
                          />
                          <span className="text-xs text-gray-500">sampai</span>
                          <input
                            type="time"
                            value={bt.endTime}
                            onChange={(e) => {
                              const val = e.target.value;
                              setBreakTimes((prev) =>
                                prev.map((item) => (item.id === bt.id ? { ...item, endTime: val } : item))
                              );
                            }}
                            className="rounded-lg border border-[#E0E6ED] bg-white px-2.5 py-1.5 text-xs text-[#0E1726]"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setBreakTimes((prev) => prev.filter((item) => item.id !== bt.id));
                            }}
                            className="text-red-500 hover:text-red-700 p-1"
                            aria-label="Hapus break"
                          >
                            <Icon icon="ph:trash-bold" className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Slot Table */}
              <div className="space-y-3">
                {/* Table Header */}
                <div className="flex items-center gap-4 text-xs font-semibold text-gray-600 px-2">
                  <div className="w-36 shrink-0">Jam</div>
                  <div className="flex-1">Weekday Sen–Jum</div>
                  <div className="flex-1">Weekend Sab–Min</div>
                </div>

                {/* Table Rows */}
                <div className="space-y-2.5">
                  {sessionRows.map((row) => {
                    const wPrice = slotPrices[row.key]?.weekday ?? 0;
                    const wePrice = slotPrices[row.key]?.weekend ?? 0;

                    return (
                      <div key={row.key} className="flex items-center gap-4">
                        {/* Jam Column */}
                        <div className="w-36 shrink-0 text-xs font-semibold text-[#0E1726]">
                          {row.label}
                        </div>

                        {/* Weekday Input */}
                        <div className="flex-1 relative">
                          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium">
                            Rp.
                          </span>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={wPrice ? wPrice.toLocaleString('id-ID') : ''}
                            onChange={(e) => {
                              const raw = Number(e.target.value.replace(/\D/g, '')) || 0;
                              handleUpdatePrice(row.key, 'weekday', raw);
                            }}
                            placeholder="0"
                            className={`w-full rounded-xl border bg-white pl-10 pr-3.5 py-2.5 text-xs sm:text-sm text-[#0E1726] focus:outline-none transition-colors ${
                              wPrice <= 0 ? 'border-red-300 focus:border-red-500' : 'border-[#E0E6ED] focus:border-[#FC480D]'
                            }`}
                          />
                        </div>

                        {/* Weekend Input */}
                        <div className="flex-1 relative">
                          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium">
                            Rp.
                          </span>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={wePrice ? wePrice.toLocaleString('id-ID') : ''}
                            onChange={(e) => {
                              const raw = Number(e.target.value.replace(/\D/g, '')) || 0;
                              handleUpdatePrice(row.key, 'weekend', raw);
                            }}
                            placeholder="0"
                            className={`w-full rounded-xl border bg-white pl-10 pr-3.5 py-2.5 text-xs sm:text-sm text-[#0E1726] focus:outline-none transition-colors ${
                              wePrice <= 0 ? 'border-red-300 focus:border-red-500' : 'border-[#E0E6ED] focus:border-[#FC480D]'
                            }`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Quick Helper Button */}
                {sessionRows.length > 1 && (
                  <div className="pt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={handleApplyFirstRowToAll}
                      className="text-xs font-semibold text-[#FC480D] hover:underline flex items-center gap-1"
                    >
                      <Icon icon="ph:copy-simple" className="w-3.5 h-3.5" />
                      <span>Terapkan harga pertama ke semua sesi</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Footer - Step 2 */}
            <div className="flex items-center justify-between pt-6 border-t border-[#F5F6F7]">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="rounded-full border border-[#D5D5D5] bg-white hover:bg-gray-50 text-[#1F1F1F] font-semibold text-sm px-6 py-2.5 transition-colors"
              >
                ← Sebelumnya
              </button>

              <div className="flex items-center gap-4">
                <span className="text-xs text-[#8C8C8C]">Langkah 2 dari 3</span>
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white font-semibold text-sm px-7 py-2.5 shadow-xs transition-colors"
                >
                  Lanjut →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ================= STEP 3: PENGATURAN LAINNYA ================= */}
        {step === 3 && (
          <div className="space-y-6">
            <h3 className="text-xs font-semibold tracking-wider text-[#8C8C8C] uppercase mb-4">
              ATURAN TAMBAHAN
            </h3>

            {/* Mode Pemesanan */}
            <div>
              <label className="block text-xs font-semibold text-[#0E1726] mb-2.5">
                Mode pemesanan
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Single session Card */}
                <div
                  onClick={() => setSingleSelection(true)}
                  className={`rounded-2xl p-4.5 p-4 cursor-pointer transition-all flex items-start gap-3.5 ${
                    singleSelection
                      ? 'border-2 border-[#FC480D] bg-[#FFF5EE]'
                      : 'border border-[#E0E6ED] bg-white hover:border-gray-300'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${
                      singleSelection
                        ? 'border-[#FC480D] bg-[#FC480D]'
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {singleSelection && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-[#0E1726]">Single session</h4>
                    <p className="text-xs text-[#8C8C8C] mt-0.5">
                      Pelanggan memesan 1 sesi per booking
                    </p>
                  </div>
                </div>

                {/* Multiple session Card */}
                <div
                  onClick={() => setSingleSelection(false)}
                  className={`rounded-2xl p-4.5 p-4 cursor-pointer transition-all flex items-start gap-3.5 ${
                    !singleSelection
                      ? 'border-2 border-[#FC480D] bg-[#FFF5EE]'
                      : 'border border-[#E0E6ED] bg-white hover:border-gray-300'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${
                      !singleSelection
                        ? 'border-[#FC480D] bg-[#FC480D]'
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {!singleSelection && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-[#0E1726]">Multiple session</h4>
                    <p className="text-xs text-[#8C8C8C] mt-0.5">
                      Pelanggan bisa memilih beberapa sesi sekaligus dalam 1 booking
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Share */}
            <div className="rounded-2xl border border-[#E0E6ED] p-4.5 p-4 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-[#0E1726]">Booking Share</h4>
                  <p className="text-xs text-[#8C8C8C] mt-0.5 max-w-xl">
                    Gabungkan 2 lapangan dalam 1 jadwal. Jika salah satu dibooking, lapangan pasangannya ikut terkunci di jam yang sama.
                  </p>
                </div>

                {/* Toggle */}
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={bookingShare}
                    onChange={(e) => {
                      setBookingShare(e.target.checked);
                      if (!e.target.checked) setPhysicalFieldSlug('');
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FC480D]"></div>
                </label>
              </div>

              {/* Physical Field selector if Booking Share is ON */}
              {bookingShare && (
                <div className="pt-2 border-t border-gray-100">
                  <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">
                    Pilih Physical Field Pasangan *
                  </label>
                  <select
                    value={physicalFieldSlug}
                    onChange={(e) => setPhysicalFieldSlug(e.target.value)}
                    className="w-full sm:w-80 rounded-xl border border-[#E0E6ED] bg-white px-3 py-2 text-xs sm:text-sm text-[#0E1726] focus:border-[#FC480D] focus:outline-none"
                  >
                    <option value="">Pilih physical field...</option>
                    {physicalFields.map((pf) => (
                      <option key={pf.slug} value={pf.slug}>
                        {pf.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Status Lapangan */}
            <div className="rounded-2xl border border-[#E0E6ED] p-4.5 p-4 flex items-center justify-between gap-4">
              <div>
                <h4 className="text-sm font-semibold text-[#0E1726]">Lapangan aktif</h4>
                <p className="text-xs text-[#8C8C8C] mt-0.5">
                  Lapangan nonaktif tidak bisa dibooking dan tidak tampil di aplikasi.
                </p>
              </div>

              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FC480D]"></div>
              </label>
            </div>

            {/* Bottom Footer - Step 3 */}
            <div className="flex items-center justify-between pt-6 border-t border-[#F5F6F7]">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="rounded-full border border-[#D5D5D5] bg-white hover:bg-gray-50 text-[#1F1F1F] font-semibold text-sm px-6 py-2.5 transition-colors"
              >
                ← Sebelumnya
              </button>

              <div className="flex items-center gap-4">
                <span className="text-xs text-[#8C8C8C]">Langkah 3 dari 3</span>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                  className="rounded-full bg-[#FC480D] hover:bg-[#E8490F] disabled:opacity-50 text-white font-semibold text-sm px-8 py-2.5 shadow-xs transition-colors flex items-center gap-2"
                >
                  {isSubmitting && <Icon icon="ph:spinner" className="w-4 h-4 animate-spin" />}
                  <span>{mode === 'create' ? 'Simpan Lapangan' : 'Perbarui Lapangan'}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldForm;
